<template>
    <CreatePopup v-if="CrisisDocDialog" v-model="CrisisDocDialog" :flowtype="'Crisis Management'"
        @create="addNewCrisis($event)" />
    <div class="sb-container">
        <div v-if="pageLoader" class="no-data">
            <RotatingLoader :size="30" :speed="0.6" label="Loading..." />
        </div>
        <div v-else class="new-crisis">
            <div class="TitleSection">
                <div class="title">Crisis Management</div>
                <button v-if="crisisList.length > 0" @click="CrisisDocDialog = true" class="new-sb-btn">
                    <Svg :src="require('@/assets/icons/add.svg')" alt="add"></Svg>
                    <span>Create</span>
                </button>
            </div>
            <div v-if="draftCount == 0 && completedCount == 0 && !filterState" class="no-crisis">
                <EmptyState :title="'No Crisis Management Document Created!'" :stage="'DRAFT'"
                    :subTitle="'Create a new Crisis Management Document to see it listed here.'"
                    @create-dialog="CrisisDocDialog = true">
                </EmptyState>
            </div>
            <div v-else class="mid-section">
                <div class="toggle-container">
                    <div class="toggle-buttons">
                        <button class="toggle-button" :class="{ active: selectedTab === 'draft' }"
                            @click="toggleTab('draft')">
                            Draft<span> ({{ draftCount }})</span>
                        </button>
                        <button class="toggle-button" :class="{ active: selectedTab === 'completed' }"
                            @click="toggleTab('completed')">
                            Complete <span>({{ completedCount }})</span>
                        </button>
                    </div>

                    <div v-if="(selectedTab === 'draft' && draftCount !== 0) || (selectedTab === 'completed' && completedCount !== 0) || filterState"
                        class="filter-Page">
                        <v-menu v-model="dateMenu" :close-on-content-click="false">
                            <template v-slot:activator="{ props }">
                                <div class="selected-date-container">
                                    <button class="filter-btn" v-bind="props">
                                        <Svg :src="require('@/assets/icons/calendar.svg')" stroke='#030712'></Svg>
                                        <span v-if="displayDateValue">{{ displayDateValue }}</span>
                                        <span class="text" v-else>Filter by Date</span>
                                    </button>
                                    <v-icon v-if="filterStartDate" @click="clearFilterDates">mdi-close</v-icon>
                                </div>
                            </template>
                            <v-date-picker v-model="selectedDate" :max="new Date()" multiple="range"
                                @update:model-value="updateFilterDate(false)">
                                <template #actions>
                                    <div class="action-btns">
                                        <v-btn @click="clearFilterDates" class="ok-btn">Cancel</v-btn>
                                        <v-btn @click="updateFilterDate(true)" class="ok-btn"
                                            :disabled="selectedDate.length === 0">OK</v-btn>
                                    </div>
                                </template>
                            </v-date-picker>
                        </v-menu>

                        <div class="view-toggle-container">
                            <button @click="selectedView = 'list'"
                                :class="{ 'active': selectedView === 'list', 'view-icon': true }">
                                <Svg :src="require('@/assets/icons/list.svg')"></Svg>
                            </button>
                            <button @click="selectedView = 'grid'"
                                :class="{ 'active': selectedView === 'grid', 'view-icon': true }">
                                <Svg :src="require('@/assets/icons/grid.svg')"></Svg>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="selectedTab === 'draft' && draftCount === 0" class="no-crisis">
                    <EmptyState :title="'No Draft Crisis Management Document!'" :stage="'DRAFT'" :filter="filterState"
                        :subTitle="filterState ? 'No Crisis Management Document match your filters..' : 'Create a new Crisis Management Document to see it listed here.'"
                        @create-dialog="CrisisDocDialog = true">
                    </EmptyState>
                </div>
                <div v-if="selectedTab === 'completed' && completedCount === 0" class="no-crisis">
                    <EmptyState :title="'No completed Crisis Management Document!'" :stage="'COMPLETED'"
                        :subTitle="filterState ? 'No Crisis Management Document match your filters..' : 'Create a new Crisis Management Document to see it listed here.'"
                        @create-dialog="CrisisDocDialog = true">
                    </EmptyState>
                </div>
                <div class="table-container" v-if="selectedView === 'list' && crisisList.length > 0">
                    <v-data-table class="table-wrapper" :headers="tableHeaders" :items="crisisList" :hover="true"
                        :items-per-page="itemsPerPage" :page="page" @click:row="redirectToDetailsPage">
                        <template v-slot:[`item.createdByName`]="{ item }">
                            <div class="avatar">
                                <img v-if="item.profileAvatar" :src="item.profileAvatar" class="profile-icon"
                                    alt="User Avatar" />
                                <div class="profile-icon no-avatar" v-else>{{ item?.createdByName?.[0] }}</div>
                                <div class="author">{{ item.createdByName }}</div>
                            </div>
                        </template>
                        <template v-slot:[`item.createdAt`]="{ item }">
                            {{ convertEpochToDate(item.createdAt) }}
                        </template>
                        <template v-slot:[`item.completedAt`]="{ item }">
                            {{ convertEpochToDate(item.completedAt) }}
                        </template>
                        <template v-slot:[`item.updatedAt`]="{ item }">
                            {{ convertEpochToDate(item.updatedAt) }}
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <v-menu v-model="item.showMoreMenu" :close-on-content-click="false">
                                <template v-slot:activator="{ props }">
                                    <div class="more-button-wrapper">
                                        <img style="height: 16px; width: 16px;"
                                            :src="require('@/assets/icons/more.svg')" alt="more" v-bind="props">
                                    </div>
                                </template>
                                <div class="more-options-container">
                                    <div @click="editCrisis(item)" class="bottom-border more-option-img-wrapper">
                                        <img :src="require('@/assets/icons/rename.svg')" alt="rename">
                                        <p>Rename</p>
                                    </div>
                                    <div class="more-option-img-wrapper" @click="deleteCrisis(item)">
                                        <Svg :src="require('@/assets/icons/delete.svg')" fill='#404040'
                                            alt="delete"></Svg>
                                        <p>Delete</p>
                                    </div>
                                </div>
                            </v-menu>
                        </template>
                    </v-data-table>
                </div>

                <div class="sb-grid" v-else-if="selectedView === 'grid' && crisisList.length > 0">
                    <div v-for="(item, index) in crisisList" :key="index" class="sb-card"
                        @click="redirectToDetailsPage(event, { item })">
                        <div class="sb-more-container">
                            <div class="sb-content">{{ item.title }}</div>
                            <div class="more-container">
                                <v-menu v-model="item.showMoreMenu" :close-on-content-click="false">
                                    <template v-slot:activator="{ props }">
                                        <img :src="require('@/assets/icons/more.svg')" alt="more" v-bind="props">
                                    </template>
                                    <div class="more-options-container">
                                        <div @click="editCrisis(item)" class="bottom-border more-option-img-wrapper">
                                            <img :src="require('@/assets/icons/rename.svg')" alt="rename">
                                            <p>Rename</p>
                                        </div>
                                        <div class="more-option-img-wrapper" @click="deleteCrisis(item)">
                                            <Svg :src="require('@/assets/icons/delete.svg')" fill='#404040'
                                                alt="delete"></Svg>
                                            <p>Delete</p>
                                        </div>
                                    </div>
                                </v-menu>
                            </div>
                        </div>
                        <div class="sb-footer">
                            <div class="avatar">
                                <img v-if="item.profileAvatar" :src="item.profileAvatar" class="profile-icon"
                                    alt="User Avatar" />
                                <div class="profile-icon no-avatar" v-else>{{ item?.createdByName?.[0] }}</div>
                                <div class="author">{{ item.createdByName }}</div>
                            </div>
                            <div class="meta-info">
                                <div class="timestamp">{{ convertUpdatedTime(item.updatedAt).date }}</div>
                                <div class="seperator"></div>
                                <div class="timestamp">{{ convertUpdatedTime(item.updatedAt).time }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
    <RenamePopup v-if="isEditing" :docName="currentRenameItem.title" :id="currentRenameItem.crisisDocumentId"
        :isEditing="isEditing" @update-isEdit="isEditing = $event" @updated-prTitle="handleTitleUpdate($event)"
        flowType="crisis" />
    <CommonPopup v-if="deleteCrisisToggle" :showCommonPopup="deleteCrisisToggle" popupType="delete"
        @confirm-action="handlePopupConfirmation" />
</template>

<<script>
import EmptyState from '@/components/EmptyState.vue';
import moment from 'moment-timezone';
import RenamePopup from '@/components/RenamePopup.vue';
import CommonPopup from '@/components/CommonPopup.vue';
import axiosInstance from '@/axiosInterceptor';
import { Eventbus } from '@/plugins/eventBus';
import RotatingLoader from '@/components/CircularProgressBar.vue';
import CreatePopup from '@/components/CreatePopup.vue';

export default {
    name: "CrisisManagementHome",
    components: {
        EmptyState, RotatingLoader, RenamePopup, CommonPopup, CreatePopup
    },
    data() {
        return {
            crisisList: [],
            CrisisDocDialog: false,
            selectedTab: null,
            selectedDate: [],
            filterStartDate: null,
            isEditing: false,
            currentRenameItem: {},
            filterEndDate: null,
            dateMenu: false,
            displayDateValue: null,
            docName: null,
            selectedView: 'grid',
            filteredDraft: [],
            filteredCompleted: [],
            pageLoader: true,
            draftCount: null,
            completedCount: null,
            newCrisisBtnLoader: false,
            isFormValid: false,
            tableHeaders: [
                { title: 'Document Name', key: 'title' },
                { title: 'Created By', key: 'createdByName' },
                { title: 'Created Date', key: 'createdAt' },
                { title: 'Last Edited', key: 'updatedAt' },
                { title: 'Actions', key: 'actions' },
            ],
            perPage: [
                { name: '5', value: 5 },
                { name: '10', value: 10 },
                { name: '25', value: 25 },
                { name: 'All', value: -1 }
            ],
            page: 1,
            itemsPerPage: 10,
            selectedCrisis: null,
            deleteCrisisToggle: false,
            filterState: false
        }
    },
    watch: {
        CrisisDocDialog() {
            if (!this.CrisisDocDialog) {
                this.resetPopupData();
            }
        }
    },
    methods: {
        async handleTitleUpdate(event) {
            this.currentRenameItem.title = event
            this.getAllCrisisDocs();
        },
        editCrisis(item) {
            this.isEditing = true;
            this.currentRenameItem = item
        },
        deleteCrisis(item) {
            this.selectedCrisis = item;
            this.deleteCrisisToggle = true;
        },
        handlePopupConfirmation(userResponse) {
            if (userResponse == 'yes') {
                axiosInstance.put('/crisis/delete', { crisisDocumentId: this.selectedCrisis.crisisDocumentId })
                    .then((response) => {
                        this.deleteCrisisToggle = false;
                        if (response.status == 200) {
                            Eventbus.emit('commonSnackbar', {
                                snackType: 'save',
                                message: 'Crisis Management Document deleted successfully'
                            });
                        }

                        this.getAllCrisisDocs();
                    })
                    .catch((error) => {
                        Eventbus.emit('commonSnackbar', {
                            snackType: 'error',
                            message: 'Error deleting Crisis Management Document',
                        });
                    })
            }
            this.deleteCrisisToggle = false;
        },
        redirectToDetailsPage($event, { item }) {
            this.$router.push({
                name: 'CrisisManagementDetails', params: {
                    crisisDocumentId: item.crisisDocumentId
                }
            });
        },
        addNewCrisis(docName) {
            this.newCrisisBtnLoader = true;
            let payload = {
                title: docName,
            }
            axiosInstance.post('/crisis/create', payload)
                .then(async (response) => {
                    if (response.status === 201) {
                        let saveDraftPayload = {
                            crisisDocumentId: response.data.data.crisisDocumentId,
                        }
                        await axiosInstance.put('/crisis/saveDraft', saveDraftPayload)
                            .then((saveDraftResponse) => {
                                Eventbus.emit('commonSnackbar', {
                                    snackType: 'save',
                                    message: 'Changes saved successfully'
                                });
                                // this.$store.dispatch('updateVisitedModules', { ...this.visitedModules, NARRATIVE: true });
                                this.$router.push({
                                    name: 'CrisisManagementDetails', params: {
                                        crisisDocumentId: response.data.data.crisisDocumentId
                                    }
                                });
                            })
                            .catch((error) => {
                                Eventbus.emit('commonSnackbar', {
                                    snackType: 'error',
                                    message: 'Failed to save changes',
                                });
                            })

                    }
                })
                .catch((error) => {
                    Eventbus.emit({
                        snackType: 'error',
                        message: 'Failed to create new Crisis Management Document'
                    })
                })
                .finally(() => {
                    this.newCrisisBtnLoader = false;
                    this.CrisisDocDialog = false;
                })

        },
        clearFilterDates() {
            this.selectedDate = [];
            this.filterStartDate = null;
            this.filterEndDate = null;
            this.dateMenu = false;
            this.displayDateValue = null;
            this.getAllCrisisDocs();
        },
        convertUpdatedDate(date) {
            return moment(date).format("Do MMM YYYY");
        },
        convertUpdatedTime(date) {
            return {
                date: moment(date).format("Do MMM, YYYY"),
                time: moment(date).format("hh:mmA")
            };
        },
        convertEpochToDate(time) {
            return moment(time).format('DD/MM/YYYY');
        },
        toggleTab(tab) {
            this.selectedTab = tab;
            this.getAllCrisisDocs();
        },
        async updateFilterDate(okBtnClicked) {
            if (this.selectedDate.length > 1) {
                this.filterStartDate = moment(this.selectedDate[0]).valueOf();
                this.filterEndDate = moment(this.selectedDate[this.selectedDate.length - 1]).valueOf();
                this.displayDateValue = moment(this.selectedDate[0]).format('DD/MM/YYYY') + ' - ' +
                    moment(this.selectedDate[this.selectedDate.length - 1]).format('DD/MM/YYYY');
                this.dateMenu = false;
                this.getAllCrisisDocs();
            } else if (okBtnClicked) {
                this.filterStartDate = moment(this.selectedDate[0]).valueOf();
                this.filterEndDate = moment(this.selectedDate[0]).endOf('day').valueOf();
                this.displayDateValue = moment(this.selectedDate[0]).format('DD/MM/YYYY');
                this.dateMenu = false;
                this.getAllCrisisDocs();
            }
        },
        async getAllCrisisDocs() {
            this.pageLoader = true;
            try {
                const response = await axiosInstance.get('/crisis', {
                    params: {
                        startDate: this.filterStartDate,
                        endDate: this.filterEndDate,
                    }
                });
                let allCrisisDocs = response.data.data;

                if (this.filterStartDate && this.filterEndDate) {
                    this.filterState = true;
                } else {
                    this.filterState = false;
                }

                this.filteredDraft = allCrisisDocs.filter(sb => sb.status === 'DRAFT').sort((a, b) => b.updatedAt - a.updatedAt);
                this.filteredCompleted = allCrisisDocs.filter(sb => sb.status === 'COMPLETED').sort((a, b) => b.updatedAt -
                    a.updatedAt);

                this.draftCount = this.filteredDraft.length;
                this.completedCount = this.filteredCompleted.length;

                this.crisisList = this.selectedTab === 'draft' ? this.filteredDraft : this.filteredCompleted;

            } catch (error) {
                if (error.response && error.response.status === 404) {

                    this.crisisList = [];
                    this.filteredDraft = [];
                    this.filteredCompleted = [];
                    this.draftCount = 0;
                    this.completedCount = 0;
                } else {
                    Eventbus.emit({
                        snackType: 'error',
                        message: 'Failed to fetch Crisis Management Document'
                    });
                }
            } finally {
                this.pageLoader = false;
            }
        },

        resetPopupData() {
            this.docName = null;
        },
        removeNumbers() {
            this.docName = this.docName.replace(/[0-9]/g, '');
        },
    },
    async mounted() {
        this.selectedTab = this.$route.query.status ? this.$route.query.status.toLowerCase() : 'draft';
        await this.getAllCrisisDocs();

    }

}
</script>

    <style scoped>
    .sb-container {
        display: flex;
        background: white;
        gap: 1rem;
        flex-grow: 1;
        border-radius: 16px;
    }

    .new-crisis {
        width: 100%;
        height: 100%;
        padding: 2rem;
        gap: 20px;
        display: flex;
        flex-direction: column;
    }

    .no-crisis {
        display: flex;
        flex-direction: column;
        padding: 2rem;
        border: 1px solid #E5E5E5;
        border-radius: 12px;
        background-color: #FAFAFA;
        gap: 20px;
        flex: 1;
    }

    .sb-grid {
        display: grid;
        overflow: auto;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1.5rem;
        will-change: transform;
    }

    .sb-card {
        display: flex;
        gap: 16px;
        border: 1px solid #E5E5E5;
        background: #FAFAFA;
        flex-direction: column;
        padding: 1rem;
        border-radius: 8px;
        cursor: pointer;
    }

    .sb-more-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .sb-content {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        font-weight: 600;
    }

    .sb-footer {
        display: flex;
        padding-top: 12px;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid #E5E5E5;
        font-size: 14px;
        color: #525252;
    }

    .no-data {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
    }

    .title {
        font-size: 2rem;
        font-weight: bold;
        border-bottom: 16px;
        font-family: 'OverpassMono';
    }

    .TitleSection {

        display: flex;
        flex-direction: row;
        justify-content: space-between;
    }

    .new-sb-btn {
        display: flex;
        background: black;
        flex-direction: row;
        color: white;
        border-radius: 8px;
        align-items: center;
        gap: 8px;
        padding: 0em 1.5em;
        font-family: 'OverpassMono';
    }

    .pr-card {
        display: flex;
        gap: 16px;
        border: 1px solid #E5E5E5;
        background: #FAFAFA;
        flex-direction: column;
        padding: 1rem;
        border-radius: 8px;
        cursor: pointer;
    }

    .pr-content {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        font-weight: 600;
    }

    .more-options-container {
        min-width: 180px;
        display: flex;
        flex-direction: column;
        background: white;
        border: 1px solid #E5E5E5;
        border-radius: 8px;
        box-shadow: 0px 4px 8px 0px #0000000F, 0px 0px 4px 0px #0000000A;
    }

    .bottom-border {
        border-bottom: 1px solid #E5E5E5;
    }

    .pr-footer {
        display: flex;
        padding-top: 12px;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid #E5E5E5;
        font-size: 14px;
        color: #525252;
    }


    .mid-section {
        display: flex;
        flex-direction: column;
        gap: 25px;
        height: 89%;
    }

    .toggle-container {
        display: flex;
        justify-content: space-between;

    }

    .toggle-buttons {
        display: flex;
        gap: 10px;
        border: 1px solid #A3A3A3;
        padding: 4px;
        border-radius: 8px;

    }

    .toggle-button {
        padding: 10px 30px;
        border-radius: 8px;
        font-family: 'OverpassMono';
    }


    .toggle-button.active {
        background-color: black;
        color: white;
    }

    .toggle-button:hover {
        background-color: black;
        color: white;
    }

    .filter-Page {
        display: flex;
        gap: 10px;
        justify-content: center;
    }

    .filter-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
    }

    .selected-date-container {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        height: 40px;
        padding: 4px 8px;
        border-radius: 8px;
        border: 1px solid #D4D4D4;
        color: #030712;
    }

    .action-btns {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .ok-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #A3A3A3;
        border-radius: 4px;
    }

    .view-toggle-container {
        display: flex;
        border: 1px solid #D4D4D4;
        height: 38px;
        border-radius: 8px;
    }

    .view-icon {
        display: flex;
        align-items: center;
        padding: 8px 16px;
    }

    .view-icon.active {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
        background-color: #F1F4FB;
    }

    .view-icon.active {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
        background-color: #F1F4FB;
    }

    .view-icon:first-child {
        border-right: 1px solid #D4D4D4;
    }

    .view-icon.active svg {
        fill: #171717;
    }

    .author {
        font-weight: 500;
    }

    .timestamp {
        font-weight: 500;
        color: #A3A3A3
    }

    .seperator {
        display: flex;
        height: 12px;
        border-left: 2px solid #ccc;
    }

    .meta-info {
        color: #ccc;
        display: flex;
        gap: 4px;
        align-items: center;
        flex-direction: row;
    }

    .more-button-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #E5E5E5;
        width: 2rem;
        height: 2rem;
        border-radius: 8px;
    }

    .more-options-contaioner {
        min-width: 180px;
        display: flex;
        flex-direction: column;
        background: white;
        border: 1px solid #E5E5E5;
        border-radius: 8px;
        box-shadow: 0px 4px 8px 0px #0000000F, 0px 0px 4px 0px #0000000A;
    }

    .more-option-img-wrapper {
        display: flex;
        gap: 8px;
        align-items: center;
        cursor: pointer;
        padding: 1rem;
    }

    .table-container {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 85%;
        justify-content: space-between;
    }

    .avatar {
        display: flex;
        align-items: center;
        flex-direction: row;
        gap: 8px;
    }

    .profile-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        width: 25px;
        height: 25px;
        overflow: hidden;
        font-size: 14px;
    }

    .no-avatar {
        background: linear-gradient(90deg, #FF00C3, #FF0A19, #FF7800);
        color: white;
    }

    .table-wrapper {
        border-radius: 8px;
        height: 100%;
        will-change: transform;
        scrollbar-width: thin;
        justify-content: center;
        align-items: end;
    }

    .table-container :deep(.v-table__wrapper tr:hover) {
        background: #F1F4FB !important;
    }

    .table-wrapper :deep(.v-table__wrapper) {
        width: 100%;
        scrollbar-width: thin;
        border-radius: 8px;
        border: 1px solid #E5E5E5;

    }

    .table-wrapper :deep(.v-table__wrapper thead) {
        position: sticky;
        top: 0;
        z-index: 1000;
        background: #F1F4FB !important;
    }

    .table-wrapper :deep(.v-table__wrapper thead tr th) {
        font-weight: 600 !important;
        color: var(--primary-font-color);
    }

    .table-wrapper :deep(.v-table__wrapper tbody) {
        max-height: 700px;
        color: var(--dark-grey-color);
    }

    .table-wrapper :deep(.v-data-table-footer .v-pagination__list .v-pagination__first) {
        display: none;
    }

    .table-wrapper :deep(.v-data-table-footer .v-pagination__list .v-pagination__last) {
        display: none;
    }

    .table-wrapper :deep(.v-data-table-footer .v-input__control) {
        height: 2rem;
        width: 4rem;
    }

    .table-wrapper :deep(.v-data-table-footer .v-input__control .v-field__input) {
        padding: 0px 16px 8px 16px;
        font-size: 14px;
    }

    .table-wrapper :deep(.v-data-table-footer .v-input__control .v-field__field) {
        height: 2rem;
        width: 3rem;
    }

    .table-wrapper :deep(.v-data-table-footer .v-input__control .v-field__append-inner) {
        width: 1rem;
    }

    .table-wrapper :deep(.v-data-table-footer .v-data-table-footer__items-per-page > .v-select) {
        width: 63px;
        background: #ffffff;
    }

    .table-wrapper :deep(.v-data-table-footer) {
        background: #F1F4FB !important;
        padding: 0rem 0rem 0rem 1rem;
        margin-top: 1rem;
        height: 5rem;
        border-radius: 8px;
        border: 1px solid #E5E5E5;
    }

    .table-wrapper :deep(.v-data-table-footer .v-input__control) {
        height: 2rem;
        width: 4rem;
    }

    .popup {
        display: flex;
        flex-direction: column;
        background: white;
        border: 1px solid #E0E9ED;
        border-radius: 8px;
    }

    .popup-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px;
        font-weight: 600;
        font-family: 'OverpassMono';
        font-size: 20px;
    }

    .optional {
        opacity: 0.4;
    }

    .circle {
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        padding: 8px;
        border: 2px solid #D4D4D4;
    }

    .popup-input {

        display: flex;
        flex-direction: column;
        gap: 12px;
        font-weight: 500;
    }

    .popup-body {
        display: flex;
        flex-direction: column;
        gap: 4em;
        padding: 16px;
        overflow: auto;
        height: 100%;
    }

    .popup-footer {
        display: flex;
        justify-content: space-between;
        gap: 1em;
    }

    .add-new-btn-continue {
        display: flex;
        align-items: center;
        gap: 25px;
        justify-content: center;
        width: 50%;
        background: #171717;
        color: white;
        border: 1px solid #525252;
        border-radius: 8px;
        font-weight: 500;
    }

    .add-new-btn {
        width: 50%;
        padding: 12px;
        background: white;
        border: 1px solid #525252;
        border-radius: 8px;
        font-weight: 500;
    }

    button:disabled {
        cursor: not-allowed;
        opacity: 0.7;
    }
</style>